import './index.css'
import mixpanel from '@disco/api/mixpanel'
import { CUSTOM_INBOXES } from '@disco/constants/FeatureTypes'
import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import { CUSTOM_INBOX, TEAM_INBOX } from '@disco/constants/GroupTypes'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import type { Group } from 'types/group'
import type { User } from 'types/user'
import { makeGetFeatureData } from '@disco/state-management/selectors/business'
import ClipboardInput from 'components/common/clipboard-input'
import GroupModal from 'components/common/custom-libraries/edit-form'
import Row from 'components/common/row'
import DeleteGroupDialog from 'components/modals/dialogs/delete-group-dialog'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import PricingBadge from 'components/ui/pricing-badge'
import type { RootState } from 'store/configureStore'
type Props = {
  groups: Group[]
  inboxType: GROUP_TYPE
  user: User
  deleteGroup: (...args: Array<any>) => any
  isFeatureEnabled?: boolean
  badgeTitle?: string
}
type State = {
  currentGroup: Group | null | undefined
  showEditModal: boolean
  showDeleteDialog: boolean
}

/**
 * Inbox list component for managing custom and team inboxes
 * Displays inbox URLs with copy functionality and management options (edit/delete)
 * Handles Mixpanel tracking for inbox interactions and modal workflows
 * @param groups - Array of group objects to display as inboxes
 * @param inboxType - Type of inbox (CUSTOM_INBOX or TEAM_INBOX)
 * @param user - Current user object
 * @param deleteGroup - Function to delete a group/inbox
 * @param isFeatureEnabled - Whether the custom inbox feature is enabled
 * @param badgeTitle - Title for pricing badge when feature is disabled
 */
class InboxList extends Component<Props, State> {
  state = {
    currentGroup: null,
    showEditModal: false,
    showDeleteDialog: false,
  }
  _toggleConfirmationDialog = (group?: Group) => {
    this.setState({
      showDeleteDialog: !this.state.showDeleteDialog,
      currentGroup: !this.state.showDeleteDialog ? group : null,
    })
  }
  _onDeleteChannel = () => {
    // Event #9 from https://disco-team.atlassian.net/browse/DISCO-4710
    if (this.state.currentGroup) {
      mixpanel.sendPureEvent('Delete Inbox', {
        type: this.state.currentGroup.group_type,
        name: this.state.currentGroup.name,
      })
    }

    this.props.deleteGroup(this.state.currentGroup)

    this._toggleConfirmationDialog()
  }

  _editGroup(group) {
    // Event #6 from https://disco-team.atlassian.net/browse/DISCO-4710
    mixpanel.sendPureEvent('Open modal', {
      modal: 'Settings',
      type: group.group_type,
      name: group.name,
      context: 'Inboxes',
    })
    this.setState({
      currentGroup: group,
      showEditModal: true,
    })
  }

  _onCloseModal = () => {
    this.setState({
      currentGroup: null,
      showEditModal: false,
    })
  }
  _onActionClick = (group, action) => {
    switch (action) {
      case 'Delete':
        this._toggleConfirmationDialog(group)

        break

      case 'Edit':
        this._editGroup(group)

        break

      default:
        throw new Error('Not implemented')
    }
  }
  _onInboxUrlCopy = group => {
    // Event #4 from https://disco-team.atlassian.net/browse/DISCO-4710
    mixpanel.sendPureEvent('Click icon', {
      type: group.group_type,
      name: group.name,
      icon: 'copy_link',
      context: 'Settings',
    })
  }
  _onInboxUpdate = () => {
    // Event #7 from https://disco-team.atlassian.net/browse/DISCO-4710
    if (this.state.currentGroup) {
      mixpanel.sendPureEvent('Save modal', {
        modal: 'Settings',
        type: this.state.currentGroup.group_type,
        name: this.state.currentGroup.name,
        context: 'Inboxes',
      })
    }

    this._onCloseModal()
  }

  render() {
    const inboxList = this.props.groups
      .filter(
        group => group.group_type === this.props.inboxType && !group.parent,
      )
      .map(group => {
        let editForm = null

        if (
          this.state.showEditModal &&
          this.state.currentGroup &&
          group.id === this.state.currentGroup.id
        ) {
          editForm = (
            <GroupModal
              {...group}
              groupType={this.props.inboxType}
              onSubmit={this._onInboxUpdate}
              onClose={this._onCloseModal}
            />
          )
        }

        const editingDisabled = !group.editable
        const deletionDisabled =
          this.props.inboxType === CUSTOM_INBOX ? editingDisabled : true
        const name =
          group.internal_name && group.internal_name.length > 0
            ? group.internal_name
            : group.name
        const uploadUrl = group.upload_url
        return (
          <Row key={group.id} columnsSize={[12]} marginBottom="small">
            <div>
              <div className="InboxList_Container">
                <div className="InboxList_Content">
                  <ClipboardInput
                    readOnly
                    label={`${name} URL`}
                    value={uploadUrl}
                    onCopy={() => this._onInboxUrlCopy(group)}
                  />
                </div>
                <div className="InboxList_ActionButtons">
                  <EllipsisMenu
                    appearance="transparent"
                    compact
                    icon="ellipsis"
                    options={[
                      {
                        label: (
                          <>
                            Edit settings{' '}
                            {!this.props.isFeatureEnabled && (
                              <PricingBadge title={this.props.badgeTitle} />
                            )}
                          </>
                        ),
                        value: 'Edit',
                        disabled:
                          editingDisabled || !this.props.isFeatureEnabled,
                      },
                      {
                        label: 'Delete inbox',
                        value: 'Delete',
                        destructive: true,
                        disabled: deletionDisabled,
                      },
                    ]}
                    hasArrow={false}
                    onOptionClick={value => this._onActionClick(group, value)}
                  />
                  {editForm}
                </div>
              </div>
            </div>
          </Row>
        )
      })
    let deleteConfirmationDialog = null

    if (this.state.showDeleteDialog && this.state.currentGroup) {
      deleteConfirmationDialog = (
        <DeleteGroupDialog
          onConfirm={this._onDeleteChannel}
          onCancel={this._toggleConfirmationDialog}
          group={this.state.currentGroup}
        />
      )
    }

    return (
      <div>
        {inboxList}
        {deleteConfirmationDialog}
      </div>
    )
  }
}

export default connect(
  (state: RootState, { inboxType }: { inboxType: GROUP_TYPE }) => {
    const getFeatureData = makeGetFeatureData()
    const { isFeatureEnabled, badgeTitle } = getFeatureData(state, {
      featureName: CUSTOM_INBOXES,
    })
    return {
      badgeTitle,
      isFeatureEnabled: inboxType === TEAM_INBOX || isFeatureEnabled,
    }
  },
)(InboxList)
